import { useEffect, useState } from 'react';

import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { useToast, useUnsubscribe } from '@hooks';
import {
    ControlledSyncfusionDatePicker,
    ControlledSyncfusionDropDownList,
    DropDownData,
} from 'components/Common/SyncfusionWrappers/Syncfusion';
import { IPreferredStatusMapping, PreferredStatusMappingSubmitMode } from 'components/ProviderData/IpaAdministration/atoms/Models';
import { PreferredStatusApi } from 'components/ProviderData/IpaAdministration/organisms/PreferredStatus/PreferredStatusApi';
import { IpaPreferredStatusService } from 'components/ProviderData/subjects/IpaPreferredStatusService';
import { currentUserService } from 'subjects/common/CurrentUserService';
import { dialogService } from 'subjects/common/DialogService';

type PreferredStatusFormProps = {
    isReadOnly: boolean;
    ipaStartDate: Date;
    ipaEndDate: Date;
    data: IPreferredStatusMapping;
    tinSource: DropDownData[];
    statusCodeSource: DropDownData[];
};
const PreferredStatusForm = ({
    isReadOnly,
    ipaStartDate,
    ipaEndDate,
    data,
    tinSource,
    statusCodeSource,
}: PreferredStatusFormProps) => {
    const [userId, setUserId] = useState('');
    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
        watch,
    } = useForm<IPreferredStatusMapping>({ mode: 'all' });
    const pushSubscription = useUnsubscribe();
    const { showSuccessToast, showErrorToast } = useToast();

    const startDate = watch('startDate');

    const onFormSubmit = (formData: IPreferredStatusMapping, mode: PreferredStatusMappingSubmitMode) => {
        dialogService.loading(true);
        const createdOrUpdateRecord =
            mode === 'create' ? PreferredStatusApi.createRecord : PreferredStatusApi.updateRecord;
        createdOrUpdateRecord(formData)
            .then(() => {
                showSuccessToast(`Preferred Status record ${mode === 'create' ? 'created' : 'updated'} successfully.`);
                dialogService.close();
                IpaPreferredStatusService.refreshGrid(formData.ipaId);
            })
            .catch((error) => {
                console.error(error);
                showErrorToast(
                    error?.response?.data ??
                    `An error occurred while ${mode === 'create' ? 'creating' : 'updating'} the Preferred Status record.`,
                    'Error',
                    10000
                );
            })
            .finally(() => dialogService.loading(false));
    };

    useEffect(() => {
        const formSubscription = IpaPreferredStatusService.submit$().subscribe((mode) => {
            handleSubmit((data) => onFormSubmit(data, mode))();
        });
        return () => formSubscription.unsubscribe();
    }, [handleSubmit, userId]);

    useEffect(() => {
        const defaultData = { ...data };
        if (!statusCodeSource.find((sc) => sc.id === defaultData.statusCode)?.id) defaultData.statusCode = '';
        if (!tinSource.find((t) => t.id === defaultData.tin)?.id) defaultData.tin = '';
        reset(defaultData);
    }, [data]);

    useEffect(() => pushSubscription(currentUserService.get().subscribe((user: any) => setUserId(user.userId))), []);

    return (
        <fieldset disabled={isReadOnly}>
            <form>
                <Row className='mb-3'>
                    <Col md='6'>
                        <label className='fw-bold mb-2'>Start Date *</label>
                        <ControlledSyncfusionDatePicker
                            format='MM/dd/yyyy'
                            name='startDate'
                            control={control}
                            dpAttrs={{
                                min: ipaStartDate,
                                max: ipaEndDate,
                                showClearButton: true,
                                placeholder: 'MM/DD/YYYY',
                                strictMode: true,
                            }}
                            rules={{
                                required: 'Start date is required',
                                validate: {
                                    validatedStartDate: (value) => {
                                        if (value && new Date(value) < new Date(ipaStartDate))
                                            return 'Preferred Status start date cannot be before IPA start date';
                                        return true;
                                    },
                                },
                            }}
                        />
                        <p className='error-form'>{errors.startDate?.message}</p>
                    </Col>
                    <Col md='6'>
                        <label className='fw-bold mb-2'>End Date *</label>
                        <ControlledSyncfusionDatePicker
                            format='MM/dd/yyyy'
                            name='endDate'
                            control={control}
                            dpAttrs={{
                                showClearButton: true,
                                placeholder: 'MM/DD/YYYY',
                                min: startDate ?? undefined,
                                max: ipaEndDate,
                                strictMode: true,
                            }}
                            rules={{
                                required: 'End date is required',
                                validate: {
                                    validatedEndDate: (value) => {
                                        if (startDate && value && new Date(value) < new Date(startDate))
                                            return 'End date cannot be earlier than start date';
                                        if (value && new Date(value) > new Date(ipaEndDate))
                                            return 'Preferred Status end date cannot be after IPA end date';
                                        return true;
                                    },
                                },
                            }}
                        />
                        <p className='error-form'>{errors.endDate?.message}</p>
                    </Col>
                </Row>

                <Row>
                    <Col md='6'>
                        <label className='fw-bold mb-2'>TIN *</label>
                        <ControlledSyncfusionDropDownList
                            name='tin'
                            control={control}
                            dataSource={tinSource}
                            rules={{ required: 'TIN is required' }}
                            ddlAttrs={{ placeholder: 'Select a TIN' }}
                        />
                        <p className='error-form'>{errors.tin?.message}</p>
                    </Col>

                    <Col md='6'>
                        <label className='fw-bold mb-2'>Preferred Status *</label>
                        <ControlledSyncfusionDropDownList
                            name='statusCode'
                            control={control}
                            dataSource={statusCodeSource}
                            rules={{ required: 'Preferred Status is required' }}
                            ddlAttrs={{ placeholder: 'Select a preferred status' }}
                        />
                        <p className='error-form'>{errors.statusCode?.message}</p>
                    </Col>
                </Row>
            </form>
        </fieldset>
    );
};

export default PreferredStatusForm;
